import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { apiClient } from '@/api/client';
import type { User, AuthResponse } from '@/types';

interface AuthState {
  user: User | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastActivity: number;
}

interface AuthActions {
  login: (name: string, role: 'student' | 'teacher') => Promise<AuthResponse>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  clearAuth: () => void;
  setError: (error: string | null) => void;
  updateActivity: () => void;
  validateSession: () => Promise<boolean>;
  checkSession: () => Promise<void>;
  giveConsent: (consentData: Record<string, boolean>) => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        sessionId: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        lastActivity: Date.now(),

        // Actions
        login: async (name: string, role: 'student' | 'teacher') => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await apiClient.authenticate(name, role);
            
            if (response.success) {
              // Handle both camelCase and snake_case responses from backend
              const user: User = {
                name: (response.userName || response.user_name) as string,
                role: (response.userRole || response.user_role) as 'student' | 'teacher',
                sessionId: (response.sessionId || response.session_id) as string,
              };

              const sessionId = response.sessionId || response.session_id;

              set({
                user,
                sessionId,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                lastActivity: Date.now(),
              });

              return response;
            } else {
              throw new Error('Authentication failed');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login failed';
            set({
              isLoading: false,
              error: errorMessage,
              isAuthenticated: false,
              user: null,
              sessionId: null,
            });
            throw error;
          }
        },

        logout: async () => {
          const { sessionId } = get();
          set({ isLoading: true, error: null });

          try {
            if (sessionId) {
              await apiClient.logout(sessionId);
            }
          } catch (error) {
            console.error('Logout error:', error);
          } finally {
            // Clear consent when logging out to ensure proper flow for new sessions
            localStorage.removeItem('consent_given');
            localStorage.removeItem('consent_time');
            
            set({
              user: null,
              sessionId: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              lastActivity: 0,
            });
          }
        },

        setUser: (user: User) => {
          set({
            user,
            sessionId: user.sessionId,
            isAuthenticated: true,
            lastActivity: Date.now(),
          });
        },

        clearAuth: () => {
          // Clear consent when clearing auth to ensure proper flow for new sessions
          localStorage.removeItem('consent_given');
          localStorage.removeItem('consent_time');
          
          set({
            user: null,
            sessionId: null,
            isAuthenticated: false,
            error: null,
            lastActivity: 0,
          });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        updateActivity: () => {
          set({ lastActivity: Date.now() });
        },

        validateSession: async () => {
          const { sessionId } = get();
          
          if (!sessionId) {
            get().clearAuth();
            return false;
          }

          try {
            const response = await apiClient.validateSession(sessionId);
            
            // Handle both old and new response formats
            const isValid = response.success ? (response.data as any)?.valid : (response as any).valid;
            const sessionData = response.success ? (response.data as any)?.sessionData : (response as any).session_data;
            
            if (isValid) {
              set({ lastActivity: Date.now() });
              
              // Update user data if available in response
              if (sessionData && get().user) {
                const updatedUser = {
                  ...get().user!,
                  name: sessionData.name || get().user!.name,
                  role: sessionData.role || get().user!.role,
                };
                set({ user: updatedUser });
              }
              
              return true;
            } else {
              get().clearAuth();
              return false;
            }
          } catch (error) {
            console.error('Session validation error:', error);
            get().clearAuth();
            return false;
          }
        },

        checkSession: async () => {
          await get().validateSession();
        },

        giveConsent: async (consentData: Record<string, boolean>) => {
          const { sessionId } = get();
          
          if (!sessionId) {
            throw new Error('No active session');
          }

          try {
            set({ isLoading: true, error: null });
            
            // Map frontend consent items to backend expected fields
            const gazeTracking = consentData.gazeTracking || false;
            const behavioralAnalysis = consentData.behavioralAnalysis || false;
            const integrityMonitoring = consentData.academicIntegrity || false;
            const cameraRecording = consentData.cameraAccess || false;
            
            // Use the correct API method that sends FormData
            const response = await apiClient.submitConsent(
              sessionId,
              gazeTracking,
              behavioralAnalysis,
              integrityMonitoring,
              cameraRecording
            );
            
            if (response.success) {
              set({ lastActivity: Date.now() });
              // Store consent in localStorage for navigation logic
              localStorage.setItem('consent_given', 'true');
              localStorage.setItem('consent_time', new Date().toISOString());
            } else {
              throw new Error(response.message || 'Failed to record consent');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to record consent';
            set({ error: errorMessage });
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },
      }),
      {
        name: 'quizsecure-auth',
        partialize: (state) => ({
          user: state.user,
          sessionId: state.sessionId,
          isAuthenticated: state.isAuthenticated,
          lastActivity: state.lastActivity,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

// Session timeout management (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in ms

// Flag to prevent multiple initializations
let authStoreInitialized = false;

// Check session validity on app load
export const initAuthStore = () => {
  // Prevent multiple initializations
  if (authStoreInitialized) {
    console.log('Auth store already initialized, skipping...');
    return;
  }
  
  authStoreInitialized = true;
  console.log('Initializing auth store...');
  
  const { lastActivity, validateSession, clearAuth } = useAuthStore.getState();
  
  // Check if session has expired
  if (lastActivity && Date.now() - lastActivity > SESSION_TIMEOUT) {
    clearAuth();
    return;
  }

  // Validate session with server if it exists
  if (useAuthStore.getState().sessionId) {
    validateSession();
  }
};

// Auto-update activity on user interactions
export const setupActivityTracking = () => {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  const updateActivity = useAuthStore.getState().updateActivity;
  
  let lastUpdate = 0;
  const throttledUpdate = () => {
    const now = Date.now();
    if (now - lastUpdate > 60000) { // Update every minute max
      lastUpdate = now;
      updateActivity();
    }
  };

  events.forEach(event => {
    document.addEventListener(event, throttledUpdate, { passive: true });
  });

  // Cleanup function
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, throttledUpdate);
    });
  };
};